import { useMemo, useRef, useState } from 'react'
import type { ComponentType, FormEvent, ReactNode, SVGProps } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { buttonVariants } from '@/components/ui/button-variants'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  Highlighter,
  Minus,
  Link as LinkIcon,
  Image,
  Table,
  Sparkles,
  MessageSquare,
  FileAudio,
  FileVideo,
  PenSquare,
  Undo,
  Redo,
  Save,
  Share2,
  Cloud,
  Timer,
  BookOpen,
  CalendarDays,
  Tag,
  Keyboard,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LoadingInline } from '@/components/ui/spinner'
const STATUS_OPTIONS = [
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'agendado', label: 'Agendado' },
  { value: 'publicado', label: 'Publicado' },
] as const

interface SermonFormData {
  titulo: string
  tema: string
  serie: string
  data: string
  referencia: string
  tags: string
  status: (typeof STATUS_OPTIONS)[number]['value']
  duracao: string
}

export function SermoesNovoPage() {
  const navigate = useNavigate()
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState<SermonFormData>({
    titulo: 'Novo Sermão sem título',
    tema: '',
    serie: '',
    data: new Date().toISOString().slice(0, 10),
    referencia: '',
    tags: '',
    status: 'rascunho',
    duracao: '35',
  })
  const [content, setContent] = useState<string>('')
  const [notas, setNotas] = useState<string>('')

  const chips = useMemo(
    () =>
      formData.tags
        .split(',')
        .map((chip) => chip.trim())
        .filter(Boolean),
    [formData.tags]
  )

  const handleCommand = (command: string, value?: string) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    document.execCommand(command, false, value ?? '')
    setContent(editorRef.current.innerHTML)
  }

  const handleBlock = (block: string) => {
    handleCommand('formatBlock', block)
  }

  const handleInsertLink = () => {
    const url = window.prompt('Informe a URL do link:')
    if (!url) return
    handleCommand('createLink', url)
  }

  const handleInsertImage = () => {
    const url = window.prompt('Informe a URL da imagem:')
    if (!url) return
    handleCommand('insertImage', url)
  }

  const handleInsertIcon = () => {
    const icon = window.prompt('Digite um emoji ou caractere especial para inserir:')
    if (!icon) return
    handleCommand('insertText', icon)
  }

  const handleInsertHorizontalRule = () => {
    handleCommand('insertHorizontalRule')
  }

  const handleTable = () => {
    const rows = Number(window.prompt('Quantidade de linhas?', '2'))
    const cols = Number(window.prompt('Quantidade de colunas?', '2'))
    if (!rows || !cols) return
    const tableHtml = `
      <table style="width:100%; border-collapse:collapse; margin:16px 0;">
        ${Array.from({ length: rows })
          .map(
            () => `
            <tr>
              ${Array.from({ length: cols })
                .map(
                  () => `
                  <td style="border:1px solid rgba(148, 163, 184, 0.4); padding:8px;">
                    &nbsp;
                  </td>
                `
                )
                .join('')}
            </tr>
          `
          )
          .join('')}
      </table>
    `
    handleCommand('insertHTML', tableHtml)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSaving(true)
    await new Promise((resolve) => setTimeout(resolve, 1200))
    console.log('Novo sermão', {
      ...formData,
      content,
      notas,
      tags: chips,
    })
    setIsSaving(false)
    navigate(-1)
  }

  const handleChange = (field: keyof SermonFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">Novo Sermão</h1>
            <p className="text-sm text-muted-foreground">
              Crie, organize e formate a mensagem antes de compartilhar com a igreja.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" className="gap-2 text-muted-foreground">
            <Cloud className="h-4 w-4" />
            Salvamento automático
          </Button>
          <Button type="button" variant="outline" className="gap-2">
            <Share2 className="h-4 w-4" />
            Compartilhar
          </Button>
        </div>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="pb-24"
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Main editor */}
          <div className="space-y-6">
            <Card className="border-none bg-gradient-to-br from-background via-background to-muted/40 shadow-none">
              <CardContent className="space-y-4 p-6">
                <div className="flex flex-col gap-3 border-b border-border/60 pb-4 md:flex-row md:items-center md:justify-between">
                  <Input
                    value={formData.titulo}
                    onChange={(event) => handleChange('titulo', event.target.value)}
                    className="border-none bg-transparent px-0 text-2xl font-semibold tracking-tight focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs uppercase tracking-wider text-primary">
                      <Sparkles className="h-3.5 w-3.5" />
                      Documento inteligente
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                      <Timer className="h-3.5 w-3.5" />
                      {formData.duracao} min
                    </div>
                  </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card px-3 py-2 shadow-sm">
                  <ToolbarGroup>
                    <ToolbarButton
                      label="Negrito"
                      onClick={() => handleCommand('bold')}
                      icon={Bold}
                    />
                    <ToolbarButton
                      label="Itálico"
                      onClick={() => handleCommand('italic')}
                      icon={Italic}
                    />
                    <ToolbarButton
                      label="Sublinhado"
                      onClick={() => handleCommand('underline')}
                      icon={Underline}
                    />
                    <ToolbarButton
                      label="Tachado"
                      onClick={() => handleCommand('strikeThrough')}
                      icon={Strikethrough}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Título"
                      onClick={() => handleBlock('h2')}
                      icon={Heading2}
                    />
                    <ToolbarButton
                      label="Normal"
                      onClick={() => handleBlock('p')}
                      icon={PenSquare}
                    />
                    <ToolbarButton
                      label="Destacar"
                      onClick={() => handleCommand('backColor', '#fff3bf')}
                      icon={Highlighter}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Alinhar à esquerda"
                      onClick={() => handleCommand('justifyLeft')}
                      icon={AlignLeft}
                    />
                    <ToolbarButton
                      label="Centralizar"
                      onClick={() => handleCommand('justifyCenter')}
                      icon={AlignCenter}
                    />
                    <ToolbarButton
                      label="Alinhar à direita"
                      onClick={() => handleCommand('justifyRight')}
                      icon={AlignRight}
                    />
                    <ToolbarButton
                      label="Justificar"
                      onClick={() => handleCommand('justifyFull')}
                      icon={AlignJustify}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Lista não ordenada"
                      onClick={() => handleCommand('insertUnorderedList')}
                      icon={List}
                    />
                    <ToolbarButton
                      label="Lista numerada"
                      onClick={() => handleCommand('insertOrderedList')}
                      icon={ListOrdered}
                    />
                    <ToolbarButton
                      label="Checklist"
                      onClick={() =>
                        handleCommand(
                          'insertHTML',
                          '<ul style="list-style-type: none; padding-left: 1.25rem;"><li>☐ Item</li></ul>'
                        )
                      }
                      icon={ListChecks}
                    />
                    <ToolbarButton
                      label="Citação"
                      onClick={() => handleBlock('blockquote')}
                      icon={Quote}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Inserir link"
                      onClick={handleInsertLink}
                      icon={LinkIcon}
                    />
                    <ToolbarButton
                      label="Inserir imagem"
                      onClick={handleInsertImage}
                      icon={Image}
                    />
                    <ToolbarButton label="Inserir tabela" onClick={handleTable} icon={Table} />
                    <ToolbarButton
                      label="Inserir divisão"
                      onClick={handleInsertHorizontalRule}
                      icon={Minus}
                    />
                    <ToolbarButton
                      label="Inserir ícone"
                      onClick={handleInsertIcon}
                      icon={Sparkles}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Adicionar comentário"
                      onClick={() =>
                        handleCommand(
                          'insertHTML',
                          '<span style="background: rgba(59,130,246,0.1); padding: 2px 6px; border-radius: 999px;">[Comentário]</span>'
                        )
                      }
                      icon={MessageSquare}
                    />
                    <ToolbarButton
                      label="Adicionar áudio"
                      onClick={() =>
                        handleCommand(
                          'insertHTML',
                          '<div style="border:1px dashed rgba(148,163,184,0.8); border-radius: 12px; padding: 16px; display:flex; align-items:center; gap:12px;">🎙️ Espaço para áudio</div>'
                        )
                      }
                      icon={FileAudio}
                    />
                    <ToolbarButton
                      label="Adicionar vídeo"
                      onClick={() =>
                        handleCommand(
                          'insertHTML',
                          '<div style="border-radius: 12px; overflow:hidden; aspect-ratio:16/9; background: linear-gradient(135deg, rgba(59,130,246,0.2), rgba(14,116,144,0.2)); display:flex; align-items:center; justify-content:center; color:rgba(59,130,246,0.9); font-weight:600;">Preview do vídeo</div>'
                        )
                      }
                      icon={FileVideo}
                    />
                  </ToolbarGroup>

                  <ToolbarGroup>
                    <ToolbarButton
                      label="Desfazer"
                      onClick={() => handleCommand('undo')}
                      icon={Undo}
                    />
                    <ToolbarButton
                      label="Refazer"
                      onClick={() => handleCommand('redo')}
                      icon={Redo}
                    />
                  </ToolbarGroup>
                </div>

                <div className="w-full rounded-2xl bg-white shadow-2xl shadow-primary/10">
                  <div className="flex items-center justify-between border-b px-6 py-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Documento
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      {new Date(formData.data).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <div className="mx-auto w-full max-w-3xl px-8 py-10">
                    <div
                      ref={editorRef}
                      className="min-h-[680px] outline-none"
                      contentEditable
                      spellCheck
                      onInput={() => setContent(editorRef.current?.innerHTML ?? '')}
                      suppressContentEditableWarning
                    >
                      <p className="text-muted-foreground">
                        Comece a digitar aqui. Use o toolbar acima para formatar o texto, inserir
                        imagens, tabelas, mídias e icones como no Google Docs.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Keyboard className="h-5 w-5 text-primary" />
                  Notas do pregador
                </CardTitle>
                <CardDescription>
                  Resumos, aplicações ou lembretes rápidos para o momento da ministração.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notas}
                  onChange={(event) => setNotas(event.target.value)}
                  placeholder="Anote pensamentos-chave, histórias de apoio ou pontos que deseja reforçar durante a mensagem."
                  className="min-h-[160px] resize-none"
                />
              </CardContent>
            </Card>
          </div>

          {/* Side panel */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-primary" />
                  Metadados da mensagem
                </CardTitle>
                <CardDescription>Defina os detalhes essenciais antes de publicar.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tema">Tema central</Label>
                  <Input
                    id="tema"
                    value={formData.tema}
                    onChange={(event) => handleChange('tema', event.target.value)}
                    placeholder="Ex.: Fé que transforma"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serie">Série ou campanha</Label>
                  <Input
                    id="serie"
                    value={formData.serie}
                    onChange={(event) => handleChange('serie', event.target.value)}
                    placeholder="Ex.: Romanos - O evangelho da justiça"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data">Data da ministração</Label>
                  <Input
                    id="data"
                    type="date"
                    value={formData.data}
                    onChange={(event) => handleChange('data', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="referencia">Referência bíblica</Label>
                  <Input
                    id="referencia"
                    value={formData.referencia}
                    onChange={(event) => handleChange('referencia', event.target.value)}
                    placeholder="Ex.: Hebreus 11:1-6"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duracao">Duração estimada (minutos)</Label>
                  <Input
                    id="duracao"
                    type="number"
                    min={10}
                    max={120}
                    value={formData.duracao}
                    onChange={(event) => handleChange('duracao', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Palavras-chave
                  </Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(event) => handleChange('tags', event.target.value)}
                    placeholder="Separe com vírgula. Ex.: fé, esperança, família"
                  />
                  {chips.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {chips.map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Estrutura sugerida
                </CardTitle>
                <CardDescription>
                  Utilize esta esqueleto como referência para equilibrar o conteúdo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <OutlineItem
                  title="Abertura"
                  description="Introdução + história conectando com a vida real."
                />
                <OutlineItem
                  title="Base bíblica"
                  description="Leia o texto principal e destaque contexto/autor."
                />
                <OutlineItem
                  title="Princípios"
                  description="Separe em 3 a 4 pontos práticos com exemplos."
                />
                <OutlineItem
                  title="Aplicação"
                  description="Como a igreja pode viver essa palavra nesta semana."
                />
                <OutlineItem
                  title="Apelo final"
                  description="Convite à reflexão, oração ou decisão específica."
                />
              </CardContent>
            </Card>

            <Card className="border-primary/30 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Sparkles className="h-5 w-5" />
                  Dicas rápidas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-primary">
                <p>✔️ Utilize subtítulos claros para cada bloco do sermão.</p>
                <p>✔️ Reforce ilustrações com imagens ou links de apoio.</p>
                <p>✔️ Registre insights durante o estudo diretamente nas notas.</p>
                <p>✔️ Salve como rascunho e refine antes de publicar.</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Floating actions */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur-md lg:left-64">
          <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 px-4 py-4 sm:px-6 lg:px-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="gap-2"
              disabled={isSaving}
              onClick={() => {
                if (editorRef.current) {
                  editorRef.current.innerHTML +=
                    '<p><strong>Próximos passos:</strong> revisar referências e compartilhar com a equipe.</p>'
                  setContent(editorRef.current.innerHTML)
                }
              }}
            >
              <Sparkles className="h-4 w-4" />
              Sugerir conclusão
            </Button>
            <Button type="submit" className="gap-2" disabled={isSaving}>
              {isSaving ? (
                <LoadingInline message="Salvando..." size="sm" />
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar sermão
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.form>
    </div>
  )
}

interface ToolbarButtonProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>
  label: string
  onClick: () => void
}

function ToolbarButton({ icon: Icon, label, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon' }),
        'h-9 w-9 rounded-lg text-muted-foreground hover:text-primary'
      )}
      title={label}
    >
      <Icon className="h-4 w-4" />
      <span className="sr-only">{label}</span>
    </button>
  )
}

interface ToolbarGroupProps {
  children: ReactNode
}

function ToolbarGroup({ children }: ToolbarGroupProps) {
  return <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1">{children}</div>
}

interface OutlineItemProps {
  title: string
  description: string
}

function OutlineItem({ title, description }: OutlineItemProps) {
  return (
    <div className="rounded-lg border border-border/40 bg-background/60 px-3 py-2">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  )
}
